import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import InfoOutline from "@mui/icons-material/InfoOutline";
import Warning from "@mui/icons-material/Warning";
import { alpha, Box, Chip, Tooltip, Typography, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useMemo } from "react";
import { useLexicalContext } from "../../lexical-context";
import {
  MEDIAN_MERGE_TAG_LENGTH,
  SHORTENED_LINK_LENGTH,
} from "../../utils/use-get-character-count";
import { SMS_MAX_CHARACTERS } from "../../utils/sms-character-count";

const SMS_GREEN_THRESHOLD = 350;
const SMS_INFO_THRESHOLD = 600;

export const SmsCharacterCount: FC = () => {
  const theme = useTheme();
  const {
    text: { characterCount },
  } = useLexicalContext();

  const {
    icon,
    backgroundColor,
    color,
  }: { icon: ReactNode; backgroundColor: string; color?: string } = useMemo(() => {
    if (!characterCount || characterCount === 0)
      return {
        icon: <InfoOutline fontSize="small" />,
        backgroundColor: theme.palette.background.default,
      };

    if (characterCount <= SMS_GREEN_THRESHOLD)
      return {
        icon: <CheckCircleOutline fontSize="small" />,
        backgroundColor: alpha(theme.palette.success.main, 0.12),
      };

    if (characterCount <= SMS_INFO_THRESHOLD)
      return {
        icon: <InfoOutline fontSize="small" />,
        backgroundColor: alpha(theme.palette.warning.main, 0.12),
      };

    if (characterCount < SMS_MAX_CHARACTERS)
      return { icon: <InfoOutline fontSize="small" />, backgroundColor: "warning.main" };

    return {
      icon: <Warning fontSize="small" />,
      backgroundColor: "error.main",
      color: theme.palette.error.contrastText,
    };
  }, [characterCount, theme.palette]);

  return (
    <Tooltip
      title={
        <>
          <Typography variant="body2" fontSize={10} fontWeight={500} sx={{ mb: 1 }}>
            The ideal SMS character count is below 350 characters. Messages over{" "}
            {SMS_MAX_CHARACTERS} characters cannot be sent.
          </Typography>
          <Typography variant="body2" fontSize={10} fontWeight={500} sx={{ mb: 1 }}>
            Some special characters, such as emojis or accented letters, count as multiple
            characters.
          </Typography>
          <Typography variant="body2" fontSize={10} fontWeight={500}>
            Merge tags count as {MEDIAN_MERGE_TAG_LENGTH} characters.
          </Typography>
          <Typography variant="body2" fontSize={10} fontWeight={500}>
            Links added with the link button count as {SHORTENED_LINK_LENGTH} characters.
          </Typography>
        </>
      }
    >
      <Box sx={{ p: 1 }}>
        <Chip
          size="small"
          icon={icon}
          label={`${characterCount ?? 0}/${SMS_MAX_CHARACTERS}`}
          sx={{
            minWidth: 120,
            position: "relative",
            "&.MuiChip-root": {
              backgroundColor,
            },
            justifyContent: "flex-start",
            "& .MuiChip-icon": {
              position: "absolute",
              left: 5,
              margin: 0,
              color,
            },
            "& .MuiChip-label": {
              paddingLeft: "25px",
              textAlign: "center",
              flex: 1,
              color,
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};
